import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, InputAdornment, LinearProgress, Tabs, Tab, Paper
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon,
    CurrencyRupee as RupeeIcon, CheckCircle as CheckIcon,
    Warning as WarningIcon, AccountBalance as AccountIcon,
    Print as PrintIcon, FilterList as FilterIcon
} from '@mui/icons-material';
import api, { studentAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const StudentFeeReport = () => {
    const [filters, setFilters] = useState({ classId: '', category: '', feeStatus: '', section: '', dateFrom: '', dateTo: '' });
    const [data, setData] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tab, setTab] = useState(0);

    useEffect(() => {
        const fetchClasses = async () => {
            try { const res = await api.get('/classes'); setClasses(res.data.data || []); } catch (e) { }
        };
        fetchClasses();
    }, []);

    const handleSearch = async () => {
        setLoading(true);
        try {
            const res = await studentAPI.getFeeReport(filters);
            setData(res.data.data || []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch fee report');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        if (filteredData.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Fee Report');

        worksheet.columns = [
            { header: 'Adm No.', key: 'admissionNumber', width: 15 },
            { header: 'Student Name', key: 'name', width: 25 },
            { header: 'Class', key: 'class', width: 15 },
            { header: 'Category', key: 'category', width: 12 },
            { header: 'Total Fee (₹)', key: 'totalFee', width: 15 },
            { header: 'Paid Fee (₹)', key: 'paidFee', width: 15 },
            { header: 'Remaining (₹)', key: 'remaining', width: 15 },
            { header: 'Status', key: 'status', width: 12 },
            { header: 'Last Payment', key: 'lastPayment', width: 15 },
            { header: 'Mode', key: 'mode', width: 12 }
        ];

        filteredData.forEach(row => {
            const lastPay = row.lastPayment && row.lastPayment !== '-' ? new Date(row.lastPayment).toLocaleDateString() : '-';
            worksheet.addRow({
                ...row,
                lastPayment: lastPay
            });
        });

        // Style header row
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1A9E8F' }
        };

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Fee_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const filteredData = data.filter(row => {
        const matchTab = tab === 0 ? true : tab === 1 ? row.status === 'Paid' : tab === 2 ? row.status === 'Partial' : row.status === 'Unpaid';
        const matchSearch = !searchTerm || row.name.toLowerCase().includes(searchTerm.toLowerCase()) || row.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchSearch;
    });

    const totalFee = data.reduce((a, r) => a + r.totalFee, 0);
    const totalPaid = data.reduce((a, r) => a + r.paidFee, 0);
    const totalRemaining = data.reduce((a, r) => a + r.remaining, 0);
    const paidCount = data.filter(r => r.status === 'Paid').length;
    const partialCount = data.filter(r => r.status === 'Partial').length;
    const unpaidCount = data.filter(r => r.status === 'Unpaid').length;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Student Fee Report</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small" onClick={() => window.print()}>Print</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small" onClick={handleExportExcel}>Export Excel</Button>
                </Box>
            </Box>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #1a9e8f' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Total Fees</Typography>
                            <Typography variant="h5" fontWeight={700}>₹{(totalFee / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Collected</Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main">₹{(totalPaid / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #e53935' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Pending</Typography>
                            <Typography variant="h5" fontWeight={700} color="error.main">₹{(totalRemaining / 1000).toFixed(0)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Collection %</Typography>
                            <Typography variant="h5" fontWeight={700}>{totalFee > 0 ? Math.round((totalPaid / totalFee) * 100) : 0}%</Typography>
                            <LinearProgress variant="determinate" value={totalFee > 0 ? (totalPaid / totalFee) * 100 : 0}
                                sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#1a9e8f' } }} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: '12px !important' }}>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <TextField select fullWidth size="small" label="Class" value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
                                <MenuItem value="">All Classes</MenuItem>
                                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField select fullWidth size="small" label="Category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                                <MenuItem value="">All</MenuItem>
                                {['OPEN', 'OBC', 'SC', 'ST', 'NT', 'VJNT'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField select fullWidth size="small" label="Fee Status" value={filters.feeStatus} onChange={(e) => setFilters({ ...filters, feeStatus: e.target.value })}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Paid">Paid</MenuItem>
                                <MenuItem value="Partial">Partial</MenuItem>
                                <MenuItem value="Unpaid">Unpaid</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField type="date" fullWidth size="small" label="From" InputLabelProps={{ shrink: true }} value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField type="date" fullWidth size="small" label="To" InputLabelProps={{ shrink: true }} value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button variant="contained" fullWidth onClick={handleSearch} disabled={loading}>{loading ? 'Loading...' : 'Search'}</Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Results */}
            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${data.length})`} />
                    <Tab label={`Paid (${paidCount})`} />
                    <Tab label={`Partial (${partialCount})`} />
                    <Tab label={`Unpaid (${unpaidCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <TextField fullWidth size="small" placeholder="Search by name or admission number..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 1.5 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Adm No</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Remaining</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Last Paid</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map(row => (
                                    <TableRow key={row.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{row.admissionNumber}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.class}</TableCell>
                                        <TableCell><Chip label={row.category} size="small" variant="outlined" /></TableCell>
                                        <TableCell align="right">₹{row.totalFee.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 600 }}>₹{row.paidFee.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ color: row.remaining > 0 ? '#e53935' : 'inherit', fontWeight: row.remaining > 0 ? 700 : 400 }}>₹{row.remaining.toLocaleString()}</TableCell>
                                        <TableCell>{row.mode}</TableCell>
                                        <TableCell>{row.lastPayment && row.lastPayment !== '-' ? new Date(row.lastPayment).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={row.status}
                                                color={row.status === 'Paid' ? 'success' : row.status === 'Partial' ? 'warning' : 'error'} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredData.length === 0 && (
                                    <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>{data.length === 0 ? 'Click "Search" to generate report' : 'No matching records found'}</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>
        </Box>
    );
};

export default StudentFeeReport;
